import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, LayoutDashboard, Inbox, UserCircle2, Briefcase } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import SandboxBanner from "@/components/SandboxBanner";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/sp/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sp-nav-dashboard" },
  { to: "/sp/leads", label: "Leads", icon: Inbox, testid: "sp-nav-leads" },
  { to: "/sp/profile", label: "Profile", icon: UserCircle2, testid: "sp-nav-profile" },
];

export default function SPShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex flex-col" data-testid="sp-shell">
      <SandboxBanner />
      <header
        className="border-b bg-white sticky top-0 z-30"
        style={{ borderColor: "var(--border)" }}
      >
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/sp/dashboard" data-testid="sp-shell-wordmark-link">
              <Wordmark size={22} />
            </Link>
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider"
              style={{
                background: "var(--blue-light)",
                color: "var(--blue-dark)",
                letterSpacing: "0.06em",
              }}
              data-testid="sp-role-badge"
            >
              <Briefcase size={11} strokeWidth={2.5} />
              Service Provider
            </span>
          </div>

          <div className="flex items-center gap-2">
            <nav className="hidden md:flex items-center gap-1 mr-2" data-testid="sp-nav">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={n.testid}>
                  {({ isActive }) => (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 font-medium"
                      style={
                        isActive
                          ? { background: "var(--blue-light)", color: "var(--blue-dark)" }
                          : undefined
                      }
                    >
                      <n.icon size={14} className="mr-1.5" />
                      {n.label}
                    </Button>
                  )}
                </NavLink>
              ))}
            </nav>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border hover:bg-slate-50"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="sp-user-menu-trigger"
                >
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold"
                    style={{ background: "var(--navy)", color: "white" }}
                  >
                    {initials}
                  </span>
                  <span
                    className="hidden sm:inline text-sm font-medium"
                    style={{ color: "var(--navy)" }}
                  >
                    {user?.name || user?.email}
                  </span>
                  <ChevronDown size={14} style={{ color: "var(--slate)" }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="text-xs font-normal" style={{ color: "var(--slate)" }}>
                    {user?.email}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  data-testid="sp-logout-btn"
                >
                  <LogOut size={14} className="mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile nav row */}
        <div
          className="md:hidden border-t bg-white px-3 py-1.5 flex gap-1 overflow-x-auto"
          style={{ borderColor: "var(--border)" }}
        >
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`${n.testid}-mobile`}
              className="text-xs px-3 py-1.5 rounded-md whitespace-nowrap font-medium"
              style={({ isActive }) => ({
                background: isActive ? "var(--blue-light)" : "transparent",
                color: isActive ? "var(--blue-dark)" : "var(--slate)",
              })}
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer
        className="border-t bg-white py-4"
        style={{ borderColor: "var(--border)" }}
      >
        <div
          className="max-w-6xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px]"
          style={{ color: "var(--tagline-grey)" }}
        >
          <span className="opstrai-tagline" style={{ fontSize: "0.6rem" }}>
            Strata operations, powered by AI
          </span>
          <span className="mono">v0.2.0-phase1 · service provider</span>
        </div>
      </footer>
    </div>
  );
}
